import {useMemo} from 'react';

import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import {
    HighlightAttributesInfo,
    HighlightAttributesItemInfo,
} from 'src/modules/event-review/typedefs';
import {AttributeUsage} from 'src/openapi/models/AttributeUsage';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {EventDetailInfo} from 'src/openapi/models/EventDetailInfo';
import {possibleValuesToString} from 'src/utils/possibleValuesToString';

export const useHighlightsAttributesInfo = (
    changeRequest: ChangeRequest,
    event: EventDetailInfo | null,
    attributeUsages: Array<AttributeUsage>,
): HighlightAttributesInfo | null => {
    return useMemo(() => {
        if (!changeRequest || isEmpty(event)) {
            return null;
        }

        const updatedAttributes = changeRequest.event.attributes.filter(attr => attr.id);

        return updatedAttributes.reduce((attributesHighlightsConfig, attribute) => {
            const actualAttribute = event?.attributes.find(attr => attr.name === attribute.name);
            const actualAttributeUsage = attributeUsages.find(usage => usage.id === attribute.id);

            const attributeHighlightConfig: HighlightAttributesItemInfo = {
                required: {
                    highlight: attribute.required !== actualAttribute?.required,
                    actualValue: !!actualAttribute?.required,
                },
                deprecated: {
                    highlight: attribute.deprecated !== actualAttribute?.deprecated,
                    actualValue: !!actualAttribute?.deprecated,
                },
                description: {
                    highlight: attribute.description !== actualAttribute?.description,
                    actualValue: actualAttribute?.description || '',
                },
                possibleValues: {
                    highlight: !isEqual(attribute.possibleValues, actualAttribute?.possibleValues) &&
                        !actualAttributeUsage,
                    actualValue: possibleValuesToString(actualAttribute?.possibleValues || []),
                },
            };

            return {...attributesHighlightsConfig, [attribute.name]: {...attributeHighlightConfig}};
        }, {});
    }, [changeRequest, event, attributeUsages]);
};
